import Stripe from 'stripe';
import {
  applyCouponAmount as applyCoupon,
  couponApplies,
} from 'utils/shared/payments/discountPolicy';
import {
  ANNUAL_ALERTS_PRICE_DEV,
  ANNUAL_ALERTS_PRICE_PROD,
  ANNUAL_DASHBOARD_PRICE_DEV,
  ANNUAL_DASHBOARD_PRICE_PROD,
  EXCLUSIVE_EVENT_ANNUAL_495_PRICE_PROD,
  LEGACY_ALERTS_ANNUAL_3_PRICE_PROD,
  ProductionExclusiveEventPrices,
} from 'constants/payments';
import { SettingsStripeSubscription } from 'utils/shared/payments/getHasActiveStripeSubscription';

export const RESUBSCRIBE_DISCOUNT_PERCENT = 30;
export const CANCEL_SAVE_DISCOUNT_PERCENT = 40;

/**
 * Every price we sell or have sold below list, mapped to the list price it is measured against. A
 * customer on one holds that discount exactly as a coupon holder does: it is their rate in every
 * comparison, it counts toward the 40% floor, and no win-back, keep offer or past-due credit is
 * ever taken off it again.
 *
 * - The post-checkout annuals. $396 against the $660 Dashboard annual is 40% off, the deepest
 *   discount we offer. $195 against the Technical Analysis annual is 44% off ($348 in production;
 *   staging's Technical Analysis list is still $600).
 * - The Exclusive Event prices: $50 a month, $135 a quarter, and $480 and $495 a year. ProfitWell's
 *   old cancellation flow reused them as its "switch to a cheaper plan" offer.
 * - A Technical Analysis annual from 2020 at $3 a year, which one customer still pays.
 *
 * A price sold below list that is missing here reads as a list price, and every offer stacks on
 * it: until 2026-09-30 the $50 plan was offered 30% off, $35 a month. When a price is minted or
 * re-minted below list (docs/checkout-upgrade-funnel.md), it belongs here, and in the dunning
 * worker's copy (google-cloud-functions `functions/stripe-dunning/params.js`).
 */
const LIST_AMOUNT_CENTS_BY_DISCOUNTED_PRICE: Record<string, number> = {
  [ANNUAL_DASHBOARD_PRICE_PROD]: 66000,
  [ANNUAL_DASHBOARD_PRICE_DEV]: 66000,
  [ANNUAL_ALERTS_PRICE_PROD]: 34800,
  [ANNUAL_ALERTS_PRICE_DEV]: 60000,
  [ProductionExclusiveEventPrices.monthly]: 7500,
  [ProductionExclusiveEventPrices.quarterly]: 19500,
  [ProductionExclusiveEventPrices.annual]: 66000,
  [EXCLUSIVE_EVENT_ANNUAL_495_PRICE_PROD]: 66000,
  [LEGACY_ALERTS_ANNUAL_3_PRICE_PROD]: 34800,
};

type PriceLike = Pick<Stripe.Price, 'id' | 'unit_amount'> | null | undefined;

/**
 * The list price a price is a discount from, or null when the price is itself a list price (or
 * anything we have no list for). Only a price below its list counts.
 */
export const getDiscountedPriceListAmountCents = (price: PriceLike): number | null => {
  const listAmountCents = price?.id ? LIST_AMOUNT_CENTS_BY_DISCOUNTED_PRICE[price.id] : undefined;
  if (!listAmountCents || typeof price?.unit_amount !== 'number') return null;
  if (price.unit_amount <= 0 || price.unit_amount >= listAmountCents) return null;
  return listAmountCents;
};

/** Whether the plan's own price is below list: nothing is ever discounted on top of it. */
export const isDiscountedPrice = (subscription: Pick<SettingsStripeSubscription, 'items'>) =>
  getDiscountedPriceListAmountCents(subscription.items.data[0]?.price) !== null;

/**
 * The list price the plan's rate is measured against, per period: the list its price is a
 * discount from, or the price itself.
 */
export const getListAmountCents = (subscription: Pick<SettingsStripeSubscription, 'items'>) => {
  const price = subscription.items.data[0]?.price;
  return getDiscountedPriceListAmountCents(price) ?? price?.unit_amount ?? 0;
};

/**
 * A forever coupon that still takes something off every renewal, or null.
 *
 * A coupon's `valid` says only whether it can still be REDEEMED. Stripe keeps billing a discount that
 * is already attached after its coupon is deleted, passes `redeem_by` or reaches `max_redemptions`,
 * so it stays the customer's rate. Reading `valid: false` as "no discount" would quote a 50% parity
 * customer "what you pay now: $75" and a 30% keep that raises them from $37.50 to $52.50.
 */
const getForeverCoupon = (
  subscription: SettingsStripeSubscription,
  price: Pick<Stripe.Price, 'product' | 'currency'> = subscription.items.data[0]?.price,
): Stripe.Coupon | null => {
  const coupon = subscription.discount?.coupon;
  return coupon &&
    coupon.duration === 'forever' &&
    price &&
    couponApplies(coupon, { ...subscription.items.data[0]?.price, ...price })
    ? coupon
    : null;
};

/**
 * A forever discount we are willing to grant, as one value.
 *
 * Every comparison against a coupon the customer already holds, every price quote, and the coupon
 * actually attached at Stripe have to agree on the same number. When the percentage was a module
 * constant, adding a second tier meant a second, deeper offer would still be compared against 30 —
 * and would preserve a 32% legacy coupon it actually beats, quoting one price and billing another.
 *
 * There is deliberately no default: a defaulted offer parameter is how a half-finished migration
 * leaves the old number alive at the one call site nobody updated.
 */
export interface DiscountOffer {
  /** The Stripe coupon attached when this offer wins. */
  couponId: string;
  /** What the offer is worth, for comparing against a discount the customer already has. */
  percent: number;
  /** Stamped into subscription metadata so the grant is identifiable after the fact. */
  metadataStamp: string;
}

const RESUMABLE_STATUSES: Stripe.Subscription.Status[] = ['active', 'trialing'];
const TERMINAL_STATUSES: Stripe.Subscription.Status[] = ['canceled', 'incomplete_expired'];

export type ResubscribeOfferMode = 'hidden' | 'resume' | 'start_new';
export type ResubscribeHiddenReason =
  | 'billing_attention_required'
  | 'current_subscription'
  | 'never_subscribed';

export interface ResubscribeOfferState {
  canStartNew: boolean;
  hiddenReason?: ResubscribeHiddenReason;
  mode: ResubscribeOfferMode;
  priorCanceledSubscription?: SettingsStripeSubscription;
  resumeCandidates: SettingsStripeSubscription[];
}

const newestFirst = (left: SettingsStripeSubscription, right: SettingsStripeSubscription) =>
  right.created - left.created;

export const isResumableSubscription = (subscription: SettingsStripeSubscription): boolean => {
  const item = subscription.items.data[0];
  const hasScheduledCancellation =
    subscription.cancel_at_period_end || typeof subscription.cancel_at === 'number';

  return (
    // A lifetime plan has no billing to win back, as the cancellation flow already holds.
    !subscription.isLifetime &&
    RESUMABLE_STATUSES.includes(subscription.status) &&
    hasScheduledCancellation &&
    !subscription.pause_collection &&
    !subscription.schedule &&
    !subscription.items.has_more &&
    subscription.items.data.length === 1 &&
    typeof item?.price?.unit_amount === 'number' &&
    !!item.price.recurring &&
    item.price.recurring.usage_type !== 'metered' &&
    (item.quantity ?? 1) === 1
  );
};

export interface ExistingForeverDiscount {
  amountOffCents: number | null;
  percentOff: number | null;
  /**
   * The plan's own price is below list (the $396 annual, the Exclusive Event prices). `percentOff`
   * is then what the customer pays against list, with any forever coupon on top compounded in. It
   * is their rate for every comparison, and nothing is ever taken off that price again.
   */
  inPrice?: boolean;
}

/**
 * The subscription's forever discount in its own terms. `getExistingForeverDiscountPercent`
 * flattens this to a percentage, which is only meaningful against the price the coupon is attached
 * to. Anything quoting money for a DIFFERENT price needs the original shape — see
 * `getDiscountedAmountCentsForPrice`.
 *
 * A price below list is measured against list, and a forever coupon on it compounds rather than
 * replacing it: a $396 annual with a 20% parity coupon pays $316.80, 52% off $660, not 20%. Reading
 * only the coupon there offered that customer 30% off $396.
 */
export const getExistingForeverDiscount = (
  subscription: SettingsStripeSubscription,
): ExistingForeverDiscount | null =>
  getForeverDiscount(subscription, getForeverCoupon(subscription));

/** Preserve Settings' embedded-price and flat-coupon rules for an eligible coupon. */
const getForeverDiscount = (
  subscription: SettingsStripeSubscription,
  coupon: Stripe.Coupon | null,
  currency = subscription.items.data[0]?.price?.currency,
): ExistingForeverDiscount | null => {
  const price = subscription.items.data[0]?.price;
  const listAmountCents = getDiscountedPriceListAmountCents(price);

  if (listAmountCents !== null && typeof price?.unit_amount === 'number') {
    const payingCents = applyCoupon(price.unit_amount, coupon, currency);
    return {
      amountOffCents: null,
      // Integer arithmetic first, so $396 against $660 is exactly 40.
      percentOff: ((listAmountCents - payingCents) * 100) / listAmountCents,
      inPrice: true,
    };
  }

  if (!coupon) {
    return null;
  }

  if (typeof coupon.percent_off === 'number') {
    return { amountOffCents: null, percentOff: coupon.percent_off };
  }

  if (
    typeof coupon.amount_off === 'number' &&
    coupon.currency === currency &&
    typeof price?.unit_amount === 'number' &&
    price.unit_amount > 0
  ) {
    return { amountOffCents: coupon.amount_off, percentOff: null };
  }

  return null;
};

export const getExistingForeverDiscountPercent = (
  subscription: SettingsStripeSubscription,
): number | null =>
  getForeverDiscountPercent(subscription, getExistingForeverDiscount(subscription));

const getForeverDiscountPercent = (
  subscription: SettingsStripeSubscription,
  discount: ExistingForeverDiscount | null,
): number | null => {
  if (!discount) {
    return null;
  }

  if (discount.percentOff !== null) {
    return discount.percentOff;
  }

  const unitAmount = subscription.items.data[0]?.price?.unit_amount;

  if (discount.amountOffCents === null || !unitAmount) {
    return null;
  }

  return Math.min(100, (discount.amountOffCents / unitAmount) * 100);
};

/**
 * The rate the plan ends up on once the offer is weighed against what it already has: its own when
 * that survives (`keepsExistingForeverDiscount`), else the offer's. A price below list always keeps
 * its own, even under the offer: the $480 annual stays at its 27.3%, never 30% off $480.
 */
export const getEffectiveResubscribeDiscountPercent = (
  subscription: SettingsStripeSubscription,
  offerPercent: number = RESUBSCRIBE_DISCOUNT_PERCENT,
  targetPrice?: Pick<Stripe.Price, 'product' | 'currency'>,
): number => {
  const discount = targetPrice
    ? getForeverDiscount(
        subscription,
        getForeverCoupon(subscription, targetPrice),
        targetPrice.currency,
      )
    : getExistingForeverDiscount(subscription);
  const percent = getForeverDiscountPercent(subscription, discount);
  return discount?.inPrice || (percent !== null && percent >= offerPercent)
    ? percent ?? offerPercent
    : offerPercent;
};

/**
 * A discount percentage the way copy prints it, the same on every surface: whole when it is whole,
 * otherwise to one decimal, and never rounded up past what is billed. A flat $25 coupon is 33.33%
 * of the $75 plan and reads "33.3"; $396 against $660 reads "40". Compare the number, print this.
 */
export const formatDiscountPercent = (percent: number): string => {
  const tenths = Math.floor(percent * 10 + 1e-6) / 10;
  return Number.isInteger(tenths) ? String(tenths) : tenths.toFixed(1);
};

/**
 * The rate a customer keeps by removing a scheduled cancellation, exactly.
 *
 * Keeping the plan keeps their own forever discount whenever it is at least the offer
 * (`buildResumeSubscriptionRequest`), so a customer saved at 40%, by the coupon or by the $396
 * annual price, stays at 40%. Every line that offers to keep a plan quotes this number, never the
 * offer's 30%, or the button promises less than the confirmation delivers. A flat coupon is its share
 * of the plan they are keeping. Round it only to print it (`formatDiscountPercent`).
 */
export const getKeepDiscountPercent = (subscription: SettingsStripeSubscription): number =>
  getEffectiveResubscribeDiscountPercent(subscription);

/**
 * Whether the customer's own forever discount survives the change: it beats the offer, or the plan's
 * price is already below list. Nothing is taken off a price below list, whatever its rate: a $50
 * plan keeps $50 rather than getting 30% off it ($35, 53% below the $75 list), and the $480 annual
 * (27.3% off) keeps $480 rather than getting 30% off $480.
 */
export const keepsExistingForeverDiscount = (
  subscription: SettingsStripeSubscription,
  offerPercent: number = RESUBSCRIBE_DISCOUNT_PERCENT,
): boolean => {
  if (isDiscountedPrice(subscription)) return true;

  const percent = getExistingForeverDiscountPercent(subscription);

  return percent !== null && percent >= offerPercent;
};

/**
 * The flat deduction a preserved coupon applies, or `null` when the discount is a percentage (or is
 * being replaced by the offer). Callers quoting money for a plan the customer is not on yet must
 * branch on this rather than assuming a ratio.
 */
export const getPreservedAmountOffCents = (
  subscription: SettingsStripeSubscription,
  offerPercent: number = RESUBSCRIBE_DISCOUNT_PERCENT,
): number | null => {
  if (!keepsExistingForeverDiscount(subscription, offerPercent)) {
    return null;
  }

  return getExistingForeverDiscount(subscription)?.amountOffCents ?? null;
};

export interface ResubscribeDiscountPlan {
  /** What the customer is billed for this price once the winning discount is applied. */
  amountCents: number;
  /** Flat deduction being preserved, for copy that must state dollars rather than a ratio. */
  preservedAmountOffCents: number | null;
  /** False means the offer coupon replaces whatever is on the subscription today. */
  keepsExistingCoupon: boolean;
}

/**
 * Which discount the customer ends up on for `targetUnitAmountCents`, and what that costs them.
 *
 * A retained coupon must apply to the target product and currency. It may become eligible only
 * after a plan change. Callers changing plans pass the target context; current-plan callers can
 * use the default. A percentage coupon scales with an eligible plan. A flat `amount_off` coupon
 * does not: it deducts the same sum from any price, so "$24 off" is 32% of the $75 monthly plan and
 * only 3.6% of the $660 annual one. Whether it still beats the offer therefore has to be decided
 * against the price being quoted, not the price it was measured against — and the customer gets
 * whichever is actually cheaper for them.
 *
 * On the plan the customer is already on this is a no-op by construction: a flat coupon is only
 * preserved when it beats the offer there, so it wins that comparison too and the resume path keeps
 * quoting their real rate.
 *
 * Callers that mutate Stripe must apply the coupon this returns. Quoting the offer price while
 * leaving a flat coupon attached would bill the difference silently at the first renewal.
 */
export const getDiscountPlanForPrice = (
  subscription: SettingsStripeSubscription,
  targetUnitAmountCents: number,
  offerPercent: number = RESUBSCRIBE_DISCOUNT_PERCENT,
  targetPrice: Pick<Stripe.Price, 'product' | 'currency'> = subscription.items.data[0]?.price,
): ResubscribeDiscountPlan => {
  const offerAmountCents = Math.round(targetUnitAmountCents * (1 - offerPercent / 100));
  const offerPlan = {
    amountCents: offerAmountCents,
    keepsExistingCoupon: false,
    preservedAmountOffCents: null,
  };
  const kept = getKeptPlanForPrice(subscription, targetUnitAmountCents, offerPercent, targetPrice);

  if (!kept) {
    return offerPlan;
  }

  // The floor holds on every new term. A deeper discount of the customer's own (a 50% parity
  // coupon) stays on the plan they are on, but it does not follow them onto another term below
  // 40% off: there the offer's own coupon applies, so annual lands at $396, never $330.
  if (
    !isOwnPrice(subscription, targetUnitAmountCents) &&
    kept.amountCents < getFloorAmountCents(targetUnitAmountCents)
  ) {
    return offerPlan;
  }

  return kept;
};

/** The deepest a price may be discounted to: 40% off, the deepest discount we offer. */
export const getFloorAmountCents = (listAmountCents: number) =>
  Math.round((listAmountCents * (100 - CANCEL_SAVE_DISCOUNT_PERCENT)) / 100);

/** Whether `targetUnitAmountCents` is the plan the customer is already on, not another term. */
const isOwnPrice = (subscription: SettingsStripeSubscription, targetUnitAmountCents: number) => {
  const item = subscription.items.data[0];
  const ownAmountCents = item?.price?.unit_amount;
  return (
    typeof ownAmountCents === 'number' &&
    targetUnitAmountCents === ownAmountCents * (item?.quantity || 1)
  );
};

/**
 * The customer's own discount applied to `targetUnitAmountCents`, when it survives the offer; null
 * when the offer's coupon replaces it.
 */
const getKeptPlanForPrice = (
  subscription: SettingsStripeSubscription,
  targetUnitAmountCents: number,
  offerPercent: number,
  targetPrice: Pick<Stripe.Price, 'product' | 'currency'>,
): ResubscribeDiscountPlan | null => {
  const offerAmountCents = Math.round(targetUnitAmountCents * (1 - offerPercent / 100));
  // A target-only coupon can become effective on this change even when it does not discount the
  // current product. Current-rate helpers filter against the current price independently.
  const coupon = getForeverCoupon(subscription, targetPrice);
  const existing = getForeverDiscount(subscription, coupon, targetPrice?.currency);
  const existingPercent = getForeverDiscountPercent(subscription, existing);

  if (
    !existing ||
    (!existing.inPrice && (existingPercent === null || existingPercent < offerPercent))
  ) {
    return null;
  }

  if (existing.inPrice) {
    // Already inside the price: the plan costs what it costs, a forever coupon on it included, and
    // no offer takes it lower.
    return {
      amountCents: Math.round(applyCoupon(targetUnitAmountCents, coupon, targetPrice.currency)),
      keepsExistingCoupon: true,
      preservedAmountOffCents: null,
    };
  }

  if (existing.percentOff !== null) {
    return {
      amountCents: Math.round(targetUnitAmountCents * (1 - existing.percentOff / 100)),
      keepsExistingCoupon: true,
      preservedAmountOffCents: null,
    };
  }

  const flatAmountCents = Math.max(0, targetUnitAmountCents - (existing.amountOffCents || 0));

  if (flatAmountCents <= offerAmountCents) {
    return {
      amountCents: flatAmountCents,
      keepsExistingCoupon: true,
      preservedAmountOffCents: existing.amountOffCents,
    };
  }

  // Their flat coupon is worth less than the offer on this plan, so the offer wins and replaces it.
  return null;
};

/** What the customer will actually be billed for `targetUnitAmountCents`. */
export const getDiscountedAmountCentsForPrice = (
  subscription: SettingsStripeSubscription,
  targetUnitAmountCents: number,
  offerPercent: number = RESUBSCRIBE_DISCOUNT_PERCENT,
  targetPrice: Pick<Stripe.Price, 'product' | 'currency'> = subscription.items.data[0]?.price,
): number =>
  getDiscountPlanForPrice(subscription, targetUnitAmountCents, offerPercent, targetPrice)
    .amountCents;

/**
 * What the next renewal will bill, per period, as it stands: the plan's price with whatever
 * discount still applies on that date. That is a forever coupon, but also a temporary one that has
 * not run out by then (the old cancellation flow handed out "40% off for 6 months" and "for 12
 * months"). An offer is weighed against this, never against list: a customer paying $396 this year
 * on a 12-month coupon was quoted "what you pay now: $660" and a $462 keep that raised their bill.
 */
export const getNextRenewalAmountCents = (subscription: SettingsStripeSubscription): number => {
  const price = subscription.items.data[0]?.price;
  const unitAmountCents = price?.unit_amount ?? 0;
  const discount = subscription.discount;
  // An attached discount bills whether or not its coupon can still be redeemed (`getForeverCoupon`).
  const coupon = discount?.coupon || null;
  // A repeating coupon discounts the invoices raised before it ends; the renewal is raised at
  // the period end. A forever coupon always applies, and a once coupon still attached has not been
  // used yet.
  const appliesToRenewal =
    !!coupon &&
    !!price &&
    couponApplies(coupon, price) &&
    (coupon.duration !== 'repeating' ||
      typeof discount?.end !== 'number' ||
      discount.end > subscription.current_period_end);

  return Math.round(
    applyCoupon(unitAmountCents, appliesToRenewal ? coupon : null, price?.currency),
  );
};

/**
 * Resolves the offer from live Stripe state. A new subscription is allowed only when every
 * existing subscription is terminal and at least one genuinely canceled subscription exists.
 * Incomplete checkouts do not count as prior subscriptions.
 */
const getResubscribeOfferState = (
  subscriptions?: SettingsStripeSubscription[] | null,
): ResubscribeOfferState => {
  const sortedSubscriptions = [...(subscriptions || [])].sort(newestFirst);
  const resumeCandidates = sortedSubscriptions.filter(isResumableSubscription);

  if (resumeCandidates.length > 0) {
    return {
      canStartNew: false,
      mode: 'resume',
      resumeCandidates,
    };
  }

  const openSubscriptions = sortedSubscriptions.filter(
    (subscription) => !TERMINAL_STATUSES.includes(subscription.status),
  );
  const priorCanceledSubscription = sortedSubscriptions.find(
    (subscription) => subscription.status === 'canceled',
  );

  if (openSubscriptions.length > 0) {
    const requiresBillingAttention = openSubscriptions.some(
      (subscription) =>
        subscription.status === 'past_due' ||
        subscription.status === 'unpaid' ||
        subscription.status === 'incomplete' ||
        !!subscription.pause_collection,
    );

    return {
      canStartNew: false,
      hiddenReason: requiresBillingAttention
        ? 'billing_attention_required'
        : 'current_subscription',
      mode: 'hidden',
      resumeCandidates: [],
    };
  }

  if (priorCanceledSubscription) {
    return {
      canStartNew: true,
      mode: 'start_new',
      priorCanceledSubscription,
      resumeCandidates: [],
    };
  }

  return {
    canStartNew: false,
    hiddenReason: 'never_subscribed',
    mode: 'hidden',
    resumeCandidates: [],
  };
};

export default getResubscribeOfferState;
